import { AxiosError } from 'axios';
import { PaginationDataModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { PaginationUtil } from '../../../../shared/components/pagination/utils/PaginationUtils';
import { left, right } from '../../../../shared/core/Either';
import { Result } from '../../../../shared/core/Result';
import { APIResponse } from '../../../../shared/infra/api/APIResponse';
import { BaseAPI } from '../../../../shared/infra/api/BaseAPI';
import { IAuthService } from '../../../users/services/authService';
import { AdminCommunityMemberResponseDTO } from '../dtos/AdminCommunityMember.dto';
import { CommunityMemberMultiRequestDTO } from '../infra/types';
import { AdminCommunityMemberModel } from '../models/CommunityMember.model';
import { AdminCommunityMemberUtil } from '../utils/CommunityMemberUtil';
import { GuestModel } from '../../../admin/members/models/Guest.model';
import { GuestsResponseDTO } from '../../../admin/members/dtos/Guest.dto';
import { GuestUtil } from '../../../admin/members/utils/GuestUtil';

export interface IAdminCommunityMembersService {
  getCommunityMembers(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<AdminCommunityMemberModel[]>>>;
  updateMemberMemberMultiStatus(
    request: CommunityMemberMultiRequestDTO,
  ): Promise<APIResponse<void>>;
  getCommunityGuests(
    offset?: number,
    filter?: string,
    dir?: number,
    deleted?: boolean,
  ): Promise<APIResponse<PaginationDataModel<GuestModel[]>>>;
}

export class AdminCommunityMemberService
  extends BaseAPI
  implements IAdminCommunityMembersService {
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error(`Method not implemented. ${error.name}`);
  }

  constructor(authService: IAuthService) {
    super(authService);
  }

  hasToken(): boolean {
    return this.authService.isUserAuthenticated();
  }

  public async getCommunityMembers(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<AdminCommunityMemberModel[]>>> {
    try {
      const response = await this.get<AdminCommunityMemberResponseDTO>({
        url: '/v1/administrator/communities/members',
        requiresToken: true,
        config: {
          params: {
            offset,
            filter,
            dir,
            search,
          },
        },
      });

      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<AdminCommunityMemberModel[]>>({
          model: data.map((member) =>
            AdminCommunityMemberUtil.maptToViewModel(member),
          ),
          meta: PaginationUtil.mapToViewModel(meta),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async updateMemberMemberMultiStatus(
    request: CommunityMemberMultiRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<CommunityMemberMultiRequestDTO>({
        url: `/v1/users/administrator/multi/${request.userId}`,
        requiresToken: true,
        payload: request,
      });
      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async getCommunityGuests(
    offset?: number | undefined,
    filter?: string | undefined,
    dir?: number | undefined,
    deleted?: boolean | undefined,
  ): Promise<APIResponse<PaginationDataModel<GuestModel[]>>> {
    try {
      const response = await this.get<GuestsResponseDTO>({
        url: 'v2/guests/admin',
        requiresToken: true,
        config: {
          params: {
            offset,
            filter,
            dir,
            deleted,
          },
        },
      });
      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<GuestModel[]>>({
          model: data.map((item) => GuestUtil.mapToViewModel(item)),
          meta: PaginationUtil.mapToViewModel(meta),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }
}
